/**
 * CompleteOutfitModal — Compléter mon look (v1.12.450+)
 *
 * REWRITE : appelle le backend POST /complete-look (Lambda dédiée) au lieu
 * du calcul local. UI groupée par catégorie manquante avec score breakdown
 * et feedback FR/EN.
 *
 * Props :
 *   visible       : bool
 *   baseItems     : pieces déjà sélectionnées (Array OutfitItem)
 *   wardrobe      : dressing user complet (Array OutfitItem)
 *   onClose       : () => void
 *   tempC, occasion, style, season : context du look
 *   gender        : 'F' | 'M' | 'unisex' (filtre catalogue côté backend)
 *   onProductPress: (product) => void (optionnel — click sur suggestion)
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Image, ActivityIndicator,
} from 'react-native';
import AppModal from './AppModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { fetchCompleteLook } from '../services/LookCompletionApiService';
import { hasUsableImage } from '../utils/imageFilter';
// v1.13.44 — masquer les images mortes (404) aussi dans "compléter un look"
import { useDeadImagesVersion } from '../utils/deadImages';

const GOLD = '#D4AF72';

const getColors = (dark) => ({
  bg:        dark ? '#0E0C0A' : '#FAFAFA',
  surface:   dark ? '#1A1814' : '#FFFFFF',
  text:      dark ? '#EEEEEE' : '#1A1814',
  textMuted: dark ? '#A89E94' : '#7A7268',
  textFaint: dark ? '#666666' : '#A89E94',
  border:    dark ? '#2A2622' : '#EAE6E0',
  gold:      GOLD,
  goldDark:  dark ? '#D4B896' : '#B8860B',
  // Score palette
  scoreHigh:    '#4CAF50',
  scoreMid:     '#FF9800',
  scoreLow:     '#F44336',
  badgeBg:      dark ? '#2A2622' : '#F0EDE7',
  // Priority colors
  required:     '#C62828',
  recommended:  '#E65100',
  optional:     '#7A7268',
});

const PRIORITY_LABEL = (priority, en) => {
  const map = en
    ? { required: 'Required', recommended: 'Recommended', optional: 'Optional' }
    : { required: 'Indispensable', recommended: 'Recommandé', optional: 'Optionnel' };
  return map[priority] || priority;
};

const CATEGORY_EMOJI = {
  Hauts: '👚', Bas: '👖', Robes: '👗', Vestes: '🧥', Manteaux: '🧥',
  Chaussures: '👠', Sacs: '👜', Bijoux: '💍', Accessoires: '🎀', Maillots: '👙',
};

function scoreColor(score, COLORS) {
  if (score >= 80) return COLORS.scoreHigh;
  if (score >= 60) return COLORS.scoreMid;
  return COLORS.scoreLow;
}

export default function CompleteOutfitModal({
  visible, baseItems, wardrobe = [], onClose,
  tempC, occasion, style, season, gender = 'F',
  onProductPress,
}) {
  const { user } = useAuth();
  const { dark } = useTheme();
  const { lang } = useI18n() || { lang: 'fr' };
  const en = lang === 'en';
  const COLORS = getColors(dark);
  const styles = useMemo(() => makeStyles(COLORS), [dark]);
  const insets = useSafeAreaInsets();
  // v1.13.44 — re-render quand une image est marquée morte → re-filtre les suggestions
  useDeadImagesVersion();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  // v1.13.27 — Appui long sur une suggestion → aperçu image en grand (comme Créer un look)
  const [zoomItem, setZoomItem] = useState(null);

  useEffect(() => {
    if (!visible || !Array.isArray(baseItems) || baseItems.length === 0) {
      setResponse(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    // v1.12.894 — GA4 : ouverture de Compléter mon look
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      trackEvent('complete_look_opened', {
        base_items_count: baseItems.length,
        wardrobe_count: Array.isArray(wardrobe) ? wardrobe.length : 0,
        gender,
        occasion: occasion || null,
        style: style || null,
        season: season || null,
        temp_c: typeof tempC === 'number' ? tempC : null,
      });
    } catch {}

    const userProfile = {
      silhouette: user?.silhouette || null,
      height_cm: user?.height_cm || null,
      weight_kg: user?.weight_kg || null,
    };

    // v1.12.496 — logs détaillés pour vérifier que les suggestions viennent du backend
    const _t0 = Date.now();
    console.log('[CompleteOutfit] REQUEST →', {
      selectedItemsCount: baseItems?.length || 0,
      selectedSample: (baseItems || []).slice(0, 3).map(i => ({
        id: i?.id, category: i?.category, name: (i?.name || '').slice(0, 30),
        partner: i?.partner, formality: i?.ai_metadata?.formality,
        primaryColor: i?.ai_metadata?.primaryColor,
      })),
      wardrobeCount: wardrobe?.length || 0,
      userProfile,
      context: { tempC, occasion, style, season },
      gender,
    });

    fetchCompleteLook({
      selectedItems: baseItems,
      wardrobe,
      userProfile,
      context: { tempC, occasion, style, season },
      gender,
      sources: ['wardrobe', 'catalogue'],
      limitPerCategory: 5,
    })
      .then((res) => {
        if (cancelled) return;
        // v1.12.496 — log réponse backend détaillée
        const ms = Date.now() - _t0;
        console.log('[CompleteOutfit] BACKEND RESPONSE ←', {
          ms,
          overallScore: res?.overallScore,
          isComplete: res?.isComplete,
          missingCategoriesCount: res?.missingCategories?.length || 0,
          stats: res?.stats,
          categories: (res?.missingCategories || []).map(c => ({
            category: c.category,
            priority: c.priority,
            suggestionsCount: c.suggestions?.length || 0,
            topScores: (c.suggestions || []).slice(0, 3).map(s => ({
              score: s.score,
              source: s.source,
              breakdown: s.scoreBreakdown,
              itemName: (s.item?.name || '').slice(0, 30),
              itemColor: s.item?.ai_metadata?.primaryColor,
            })),
          })),
        });
        setResponse(res);
        // v1.12.894 — GA4 : Compléter mon look terminé avec succès
        try {
          const { trackEvent } = require('../services/AnalyticsService');
          trackEvent('complete_look_completed', {
            overall_score: typeof res?.overallScore === 'number' ? res.overallScore : null,
            is_complete: !!res?.isComplete,
            missing_categories_count: Array.isArray(res?.missingCategories) ? res.missingCategories.length : 0,
            duration_ms: Date.now() - _t0,
          });
        } catch {}
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'failed');
        console.warn('[CompleteOutfit] BACKEND ERROR:', e?.message);
        // v1.12.894 — GA4 : échec
        try {
          const { trackEvent } = require('../services/AnalyticsService');
          trackEvent('complete_look_failed', { reason: String(e?.message || 'unknown').slice(0, 80) });
        } catch {}
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [visible, baseItems, wardrobe, tempC, occasion, style, season, gender, user?.silhouette]);

  if (!visible) return null;

  return (
    <AppModal visible={visible} onClose={onClose} animationType="slide">
      <View style={[styles.container, { paddingTop: insets.top }]}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <Text style={styles.closeText}>✕</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{en ? 'Complete my look' : 'Compléter mon look'}</Text>
          <View style={{ width: 32 }} />
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {loading && (
            <View style={styles.loaderBox}>
              <ActivityIndicator size="large" color={COLORS.gold} />
              <Text style={styles.loaderText}>
                {en ? 'Our AI is analyzing your look...' : 'Notre IA analyse votre look...'}
              </Text>
            </View>
          )}

          {!loading && error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>
                {en ? 'Could not load suggestions' : 'Impossible de charger les suggestions'}
              </Text>
              <Text style={styles.errorMsg}>{error}</Text>
            </View>
          )}

          {!loading && !error && response && (
            <>
              {/* Header score global */}
              <View style={styles.overallCard}>
                <View style={styles.overallRow}>
                  <View style={[styles.scoreCircle, { borderColor: scoreColor(response.overallScore, COLORS) }]}>
                    <Text style={[styles.scoreCircleText, { color: scoreColor(response.overallScore, COLORS) }]}>
                      {response.overallScore}
                    </Text>
                  </View>
                  <View style={styles.overallTextBox}>
                    <Text style={styles.overallTitle}>
                      {response.isComplete
                        ? (en ? 'Look complete' : 'Look complet')
                        : (en ? 'Almost there' : 'Presque terminé')}
                    </Text>
                    <Text style={styles.overallFeedback}>
                      {en ? response.overallFeedbackEn : response.overallFeedback}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Sections par catégorie manquante */}
              {response.missingCategories.map((cat) => {
                // v1.13.44 — Exclure les suggestions dont l'image est morte (404),
                // bloquée ou vide → jamais de vignette cassée dans "compléter un look".
                const usableSugs = (cat.suggestions || []).filter((sg) => hasUsableImage(sg.item));
                return (
                <View key={cat.category} style={styles.categorySection}>
                  <View style={styles.categoryHeader}>
                    <Text style={styles.categoryTitle}>
                      {CATEGORY_EMOJI[cat.category] || ''} {cat.category}
                    </Text>
                    <View style={[styles.priorityBadge, { backgroundColor: COLORS[cat.priority] || COLORS.optional }]}>
                      <Text style={styles.priorityText}>{PRIORITY_LABEL(cat.priority, en)}</Text>
                    </View>
                  </View>
                  <Text style={styles.categoryReason}>{en ? cat.reasonEn : cat.reason}</Text>

                  {usableSugs.length === 0 ? (
                    <View style={styles.emptySuggestions}>
                      <Text style={styles.emptyText}>
                        {en ? 'No compatible item found' : 'Aucune pièce compatible trouvée'}
                      </Text>
                    </View>
                  ) : (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.suggestionRow}>
                      {usableSugs.map((sug) => (
                        <TouchableOpacity
                          key={sug.item?.id || Math.random()}
                          style={styles.suggestionCard}
                          onPress={() => onProductPress?.(sug.item)}
                          onLongPress={() => sug.item?.image_url && setZoomItem(sug.item)}
                          delayLongPress={250}
                          activeOpacity={0.8}
                        >
                          {sug.item?.image_url ? (
                            <View>
                              <Image source={{ uri: sug.item.image_url }} style={styles.suggestionImage} />
                              {/* v1.13.27 — signal "appui long = agrandir" */}
                              <View style={styles.zoomHintBadge} pointerEvents="none">
                                <Text style={styles.zoomHintIcon}>⤢</Text>
                              </View>
                            </View>
                          ) : (
                            <View style={styles.suggestionImagePlaceholder}>
                              <Text style={{ fontSize: 32 }}>{CATEGORY_EMOJI[cat.category]}</Text>
                            </View>
                          )}
                          {/* Score badge */}
                          <View style={[styles.suggestionScoreBadge, { backgroundColor: scoreColor(sug.score, COLORS) }]}>
                            <Text style={styles.suggestionScoreText}>{sug.score}</Text>
                          </View>
                          {/* Source badge */}
                          <View style={[styles.suggestionSourceBadge, { backgroundColor: sug.source === 'wardrobe' ? COLORS.gold : COLORS.surface }]}>
                            <Text style={styles.suggestionSourceText}>
                              {sug.source === 'wardrobe'
                                ? (en ? 'Wardrobe' : 'Dressing')
                                : (en ? 'Store' : 'Boutique')}
                            </Text>
                          </View>
                          <Text style={styles.suggestionName} numberOfLines={1}>
                            {sug.item?.name || ''}
                          </Text>
                          <Text style={styles.suggestionFeedback} numberOfLines={2}>
                            {en ? sug.feedbackEn : sug.feedback}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  )}
                </View>
                );
              })}

              {/* Stats footer (debug) */}
              {response.stats && (
                <Text style={styles.statsText}>
                  {response.stats.suggestionsReturned} {en ? 'suggestions' : 'suggestions'} · {response.stats.durationMs}ms
                </Text>
              )}
            </>
          )}
        </ScrollView>

        {/* v1.13.27 — Overlay aperçu image en grand (appui long sur une suggestion) */}
        {zoomItem && (
          <TouchableOpacity
            style={styles.zoomOverlay}
            activeOpacity={1}
            onPress={() => setZoomItem(null)}
          >
            <Image source={{ uri: zoomItem.image_url }} style={styles.zoomImg} resizeMode="contain" />
            {!!zoomItem.name && <Text style={styles.zoomName} numberOfLines={2}>{zoomItem.name}</Text>}
            <Text style={styles.zoomHint}>{en ? 'Tap to close' : 'Touchez pour fermer'}</Text>
          </TouchableOpacity>
        )}
      </View>
    </AppModal>
  );
}

const makeStyles = (COLORS) => StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12,
    backgroundColor: COLORS.surface,
    borderBottomWidth: 1, borderBottomColor: COLORS.border,
  },
  closeBtn: { width: 32, height: 32, justifyContent: 'center', alignItems: 'center' },
  closeText: { fontSize: 22, color: COLORS.text, fontWeight: '300' },
  headerTitle: { fontSize: 17, fontWeight: '600', color: COLORS.text, letterSpacing: 0.3 },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 40 },

  loaderBox: { paddingVertical: 60, alignItems: 'center' },
  loaderText: { marginTop: 16, color: COLORS.textMuted, fontSize: 14 },

  errorBox: { padding: 16, backgroundColor: COLORS.surface, borderRadius: 12, marginVertical: 16 },
  errorText: { color: COLORS.scoreLow, fontWeight: '600', fontSize: 14, marginBottom: 4 },
  errorMsg: { color: COLORS.textMuted, fontSize: 12 },

  overallCard: {
    backgroundColor: COLORS.surface, borderRadius: 16, padding: 16,
    marginBottom: 20, borderWidth: 1, borderColor: COLORS.border,
  },
  overallRow: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  scoreCircle: {
    width: 64, height: 64, borderRadius: 32, borderWidth: 3,
    justifyContent: 'center', alignItems: 'center',
  },
  scoreCircleText: { fontSize: 22, fontWeight: '700' },
  overallTextBox: { flex: 1 },
  overallTitle: { fontSize: 16, fontWeight: '600', color: COLORS.text, marginBottom: 4 },
  overallFeedback: { fontSize: 13, color: COLORS.textMuted, lineHeight: 18 },

  categorySection: { marginBottom: 24 },
  categoryHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  categoryTitle: { fontSize: 16, fontWeight: '600', color: COLORS.text },
  priorityBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  priorityText: { fontSize: 10, color: '#FFFFFF', fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase' },
  categoryReason: { fontSize: 12, color: COLORS.textMuted, marginBottom: 10, fontStyle: 'italic' },

  emptySuggestions: { padding: 20, alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 12 },
  emptyText: { color: COLORS.textFaint, fontSize: 12 },

  suggestionRow: { paddingVertical: 4 },
  suggestionCard: {
    width: 150, backgroundColor: COLORS.surface, borderRadius: 12,
    marginRight: 10, padding: 8, borderWidth: 1, borderColor: COLORS.border,
  },
  suggestionImage: { width: '100%', height: 130, borderRadius: 8, backgroundColor: COLORS.badgeBg },
  // v1.13.27 — signal "appui long = agrandir" (badge loupe en bas-gauche de l'image)
  zoomHintBadge: {
    position: 'absolute', bottom: 6, left: 6,
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center', justifyContent: 'center',
  },
  zoomHintIcon: { color: '#FFFFFF', fontSize: 12, fontWeight: '700' },
  // Overlay aperçu grand format
  zoomOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.92)',
    alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 50,
  },
  zoomImg: { width: '100%', height: '70%' },
  zoomName: { color: '#FFFFFF', fontSize: 15, fontWeight: '600', marginTop: 16, textAlign: 'center' },
  zoomHint: { color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 8 },
  suggestionImagePlaceholder: {
    width: '100%', height: 130, borderRadius: 8,
    backgroundColor: COLORS.badgeBg, justifyContent: 'center', alignItems: 'center',
  },
  suggestionScoreBadge: {
    position: 'absolute', top: 12, right: 12,
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10,
  },
  suggestionScoreText: { color: '#FFFFFF', fontSize: 11, fontWeight: '700' },
  suggestionSourceBadge: {
    position: 'absolute', top: 12, left: 12,
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8,
  },
  suggestionSourceText: { color: COLORS.text, fontSize: 9, fontWeight: '600', letterSpacing: 0.3 },
  suggestionName: { marginTop: 6, fontSize: 12, fontWeight: '600', color: COLORS.text },
  suggestionFeedback: { marginTop: 2, fontSize: 10, color: COLORS.textMuted, lineHeight: 14, minHeight: 28 },

  statsText: { fontSize: 10, color: COLORS.textFaint, textAlign: 'center', marginTop: 12 },
});
